import { View } from '@react-pdf/renderer';
import React from 'react';
import { generate } from 'shortid';
import FlexTableRow from './components/FlexTableRow';
import ContinuedTextIndicator from './components/ContinuedTextIndicator';
import { FlexTableProps } from './types';
import styles from './styles';
import useFlexTable from './hooks/useFlexTable';
import HeadingRows from './components/HeadingRows';

const FlexTable: React.FC<FlexTableProps> = ({
  headerText,
  tableRows,
  continuedText,
  headingStyles = {},
  cellStyles = {},
  rowBordered = true,
  cellBordered = false,
  wrap = true,
  headerContinuedText = 'Continued',
  introContent
}) => {
  const { tableState } = useFlexTable();

  const rows = [[{ label: headerText }], ...tableRows];

  // The first two rows should never be displayed in different pages
  const headingRows = rows.slice(0, 2);
  const bodyRows = rows.slice(2);

  return (
    <View wrap={wrap}>
      {/* Table body rows */}
      {bodyRows.length > 0 && (
        <View style={styles.tableContainer}>
          {bodyRows.map((row, rowIndex) => {
            const currentRowIndex = rowIndex + 2;
            const isLastRow = rowIndex === bodyRows.length - 1;
            const headingRowsToRender = row[0].isCustom
              ? headingRows.slice(0, 1)
              : headingRows;

            return (
              <View key={`table-row-${generate()}`} wrap={false}>
                {rowIndex === 0 && introContent}
                {/* Header that appears on every page with table rows if the previous row is on a different page */}
                <HeadingRows
                  tableState={tableState}
                  headingRows={headingRowsToRender}
                  rowIndex={currentRowIndex}
                  headingStyles={headingStyles}
                  cellStyles={cellStyles}
                  rowBordered={rowBordered}
                  cellBordered={cellBordered}
                  headerContinuedText={headerContinuedText}
                />
                <FlexTableRow
                  row={row}
                  rowIndex={currentRowIndex}
                  cellStyles={cellStyles}
                  tableState={tableState}
                  rowBordered={rowBordered}
                  cellBordered={cellBordered}
                  isLast={isLastRow}
                />
                {/* Continued text indicator - shows only if this row is last on its page and next page has rows */}
                {continuedText && (
                  <ContinuedTextIndicator
                    rowIndex={currentRowIndex}
                    continuedText={continuedText}
                    tableState={tableState}
                    hasNextRow={rowIndex < bodyRows.length - 1}
                  />
                )}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

export default FlexTable;
